import { useEffect, useState } from 'react';

import { Calculator } from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { CartaoDeConfiguracao } from './CartaoDeConfiguracao';
import { CHAVES, useConfiguracao, type ParametrosDeAteste } from './dados';

const DESVIO_VALOR_PADRAO = 20;
const DESVIO_ALUNOS_PADRAO = 15;
const BASELINE_PADRAO = 6;

function limitarPercentual(valor: number, padrao: number): number {
  if (!Number.isFinite(valor)) {
    return padrao;
  }
  return Math.max(1, Math.min(100, Math.trunc(valor)));
}

function limitarMeses(valor: number): number {
  if (!Number.isFinite(valor)) {
    return BASELINE_PADRAO;
  }
  return Math.max(1, Math.min(24, Math.trunc(valor)));
}

/**
 * `/admin/configuracoes/ateste` — os limites que fazem o simulador de
 * ateste sinalizar uma medição como anormal.
 *
 * São três parâmetros: quanto o valor pode desviar da média histórica do
 * credenciado, quanto a quantidade de alunos transportados pode desviar, e
 * quantos meses entram no cálculo dessa média.
 *
 * "Salvar" não grava — ver `CartaoDeConfiguracao.tsx`.
 */
export function AtesteParametrosPage() {
  const consulta = useConfiguracao<ParametrosDeAteste>(CHAVES.ateste);

  const [desvioValor, setDesvioValor] = useState(DESVIO_VALOR_PADRAO);
  const [desvioAlunos, setDesvioAlunos] = useState(DESVIO_ALUNOS_PADRAO);
  const [baselineMeses, setBaselineMeses] = useState(BASELINE_PADRAO);

  useEffect(() => {
    if (consulta.data) {
      setDesvioValor(limitarPercentual(consulta.data.desvio_valor_pct, DESVIO_VALOR_PADRAO));
      setDesvioAlunos(limitarPercentual(consulta.data.desvio_alunos_pct, DESVIO_ALUNOS_PADRAO));
      setBaselineMeses(limitarMeses(consulta.data.baseline_meses));
    }
  }, [consulta.data]);

  function validar(): boolean {
    const percentuaisForaDaFaixa = [desvioValor, desvioAlunos].some(
      (pct) => !Number.isInteger(pct) || pct < 1 || pct > 100,
    );
    if (percentuaisForaDaFaixa) {
      toast.error('Percentual inválido', {
        description: 'Os dois limites de desvio precisam ficar entre 1 e 100.',
      });
      return false;
    }
    if (!Number.isInteger(baselineMeses) || baselineMeses < 1 || baselineMeses > 24) {
      toast.error('Janela inválida', {
        description: 'A janela do histórico precisa ficar entre 1 e 24 meses.',
      });
      return false;
    }
    return true;
  }

  return (
    <CartaoDeConfiguracao
      titulo="Ateste de medição — parâmetros"
      descricao="Define os limites de desvio que sinalizam medição anormal no simulador e a janela de meses usada como média histórica de cada credenciado."
      tituloDoCartao="Limites e janela"
      icone={Calculator}
      carregando={consulta.isLoading}
      comErro={consulta.isError}
      mensagemDeErro="Não foi possível carregar os parâmetros do ateste agora. Verifique sua conexão e tente novamente."
      aoTentarDeNovo={() => void consulta.refetch()}
      aoValidar={validar}
    >
      <div className="space-y-2">
        <Label htmlFor="ateste-desvio-valor">Desvio de valor</Label>
        <div className="flex items-center gap-2">
          <Input
            id="ateste-desvio-valor"
            type="number"
            min={1}
            max={100}
            value={desvioValor}
            onChange={(evento) =>
              setDesvioValor(limitarPercentual(Number(evento.target.value), DESVIO_VALOR_PADRAO))
            }
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Acima desta variação contra a média histórica, a linha do credenciado recebe alerta de
          desvio de valor. Padrão: {DESVIO_VALOR_PADRAO}%.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ateste-desvio-alunos">Desvio de quantidade de alunos</Label>
        <div className="flex items-center gap-2">
          <Input
            id="ateste-desvio-alunos"
            type="number"
            min={1}
            max={100}
            value={desvioAlunos}
            onChange={(evento) =>
              setDesvioAlunos(limitarPercentual(Number(evento.target.value), DESVIO_ALUNOS_PADRAO))
            }
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Mesma ideia, para a quantidade de crianças transportadas. Padrão: {DESVIO_ALUNOS_PADRAO}%.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ateste-baseline">Janela do histórico</Label>
        <div className="flex items-center gap-2">
          <Input
            id="ateste-baseline"
            type="number"
            min={1}
            max={24}
            value={baselineMeses}
            onChange={(evento) => setBaselineMeses(limitarMeses(Number(evento.target.value)))}
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">meses</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Quantos meses anteriores entram na média móvel. Padrão: {BASELINE_PADRAO} — o suficiente
          para a média absorver a sazonalidade do calendário letivo.
        </p>
      </div>
    </CartaoDeConfiguracao>
  );
}

export default AtesteParametrosPage;
